import React, { useState } from 'react';
import { Button, ToggleButton } from '@fluentui/react-components';
import { ChevronDown16Regular, ChevronUp16Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';

import { CONCEPT_VERDICTS, ConceptVerdict, ConceptVerdictItem, ContractReview } from '../../../../models/Entities/Contracts/ContractReview';
import { GuideEmptyState } from '../../../../theme/components/GuideEmptyState';
import { GuideSectionTitle } from '../../../../theme/components/GuideSectionTitle';
import { CheckStatusPill, Pill, VerdictPill } from '../ContractReviewPills/ContractReviewPills.Component';
import { useHumanizeText } from '../ContractReview.Text';

/*
 * "Concepto por concepto": una tarjeta por concepto con su veredicto en palabras, qué no coincide, los chequeos plegados
 * y un botón que lleva a sus cambios y avisos en la otra solapa.
 */

export interface ContractReviewConceptsTabProps {
  review: ContractReview;
  onViewFindings: (verdict: ConceptVerdictItem) => void;
}

const conceptKey = (v: ConceptVerdictItem, index: number) => (v.contractConceptId !== null ? `c-${v.contractConceptId}` : `n-${index}`);

export const ContractReviewConceptsTab: React.FC<ContractReviewConceptsTabProps> = ({ review, onViewFindings }) => {
  const { t } = useTranslation();
  const [verdictFilter, setVerdictFilter] = useState<ConceptVerdict | undefined>(undefined);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const humanize = useHumanizeText();

  if (!review.scopes.includes('ConceptReview')) return <GuideEmptyState title={t('contractReview.concepts.notIncluded')} />;

  const verdicts = review.conceptVerdicts ?? [];
  const shown = verdictFilter ? verdicts.filter(v => v.verdict === verdictFilter) : verdicts;
  const withObservations = verdicts.filter(v => v.verdict !== 'Ok').length;
  const checkName = (code: string) => t(`contractReview.check.${code}.name`, code);

  return (
    <div className="crv-concepts">
      <GuideSectionTitle title={t('contractReview.concepts.title')} description={t('contractReview.concepts.description')} />
      <div className="crv-concepts__bar">
        <div className="crv-chips" role="group" aria-label={t('contractReview.concepts.filterLabel')}>
          <ToggleButton size="small" shape="circular" checked={verdictFilter === undefined} onClick={() => setVerdictFilter(undefined)}>
            {t('contractReview.concepts.filterAll', { count: verdicts.length })}
          </ToggleButton>
          {CONCEPT_VERDICTS.map(v => (
            <ToggleButton key={v} size="small" shape="circular" checked={verdictFilter === v} onClick={() => setVerdictFilter(verdictFilter === v ? undefined : v)}>
              {t('contractReview.concepts.filterVerdict', { verdict: t(`contractReview.verdict.${v}`, v), count: verdicts.filter(x => x.verdict === v).length })}
            </ToggleButton>
          ))}
        </div>
        <span className="crv-toolbar__count">{t('contractReview.concepts.observationsCount', { count: withObservations })}</span>
      </div>

      {shown.length === 0 ? (
        <GuideEmptyState title={t('contractReview.work.empty.filteredTitle')} />
      ) : (
        <div className="crv-list" role="list" aria-label={t('contractReview.concepts.title')} data-testid="concepts-list">
          {shown.map((verdict, index) => {
            const key = conceptKey(verdict, index);
            const isOpen = !!expanded[key];
            const observed = verdict.checks.filter(c => c.status === 'Warning' || c.status === 'Error');
            return (
              <article key={key} role="listitem" className={`crv-concept crv-concept--${verdict.verdict}`} data-testid={`concept-row-${key}`}>
                <div className="crv-concept__head">
                  <div className="crv-concept__title-row">
                    <h3 className="crv-concept__title">{verdict.label}</h3>
                    {verdict.contractConceptId === null && <Pill tone="confirmed" text={t('contractReview.concepts.new')} />}
                    <VerdictPill verdict={verdict.verdict} />
                  </div>
                  <Button size="small" disabled={verdict.findingIds.length === 0} onClick={() => onViewFindings(verdict)}>
                    {t('contractReview.concepts.viewFindings', { count: verdict.findingIds.length })}
                  </Button>
                </div>
                {observed.length === 0 ? (
                  <p className="crv-item__muted">{t('contractReview.concepts.allOk')}</p>
                ) : (
                  <p className="crv-concept__issues">
                    <span className="crv-concept__issues-label">{t('contractReview.concepts.issues')}</span> {observed.map(c => checkName(c.check)).join(', ')}
                  </p>
                )}
                {verdict.checks.length > 0 && (
                  <Button
                    size="small"
                    appearance="transparent"
                    className="crv-item__more"
                    icon={isOpen ? <ChevronUp16Regular /> : <ChevronDown16Regular />}
                    aria-expanded={isOpen}
                    onClick={() => setExpanded({ ...expanded, [key]: !isOpen })}
                  >
                    {isOpen ? t('contractReview.concepts.collapse', { label: verdict.label }) : t('contractReview.concepts.expand', { label: verdict.label })}
                  </Button>
                )}
                {isOpen && (
                  <ul className="crv-checks" aria-label={t('contractReview.concepts.collapse', { label: verdict.label })}>
                    {verdict.checks.map(check => (
                      <li key={check.check} className={`crv-check crv-check--${check.status}`}>
                        <CheckStatusPill status={check.status} />
                        <span className="crv-check__name" title={t(`contractReview.check.${check.check}.description`, '')}>
                          {checkName(check.check)}
                        </span>
                        {check.detail && <span className="crv-check__detail">{humanize(check.detail)}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ContractReviewConceptsTab;
